<template>
  <div class="Item">
    <div class="Product">
      <NuxtLink :to="productPageLink" @click="$emit('leaveCart')" class="ProductImageWrapper">
        <PMCardImage
          :alt="item.product.product_name"
          loading="lazy"
          :url="item.product.pm_card.card_image1_url"
        />
      </NuxtLink>
      <div class="ItemInfo">
        <NuxtLink
          :to="productPageLink"
          class="ProductName"
          @click="$emit('leaveCart')"
        >
          {{ item.product.product_name }}
        </NuxtLink>
        <div class="ProductInfo">
          <ConditionLabel :status="item.rank" />
          <div>{{ cardInfoString }}</div>
        </div>
        <div class="UnitPrice">
          <span class="Price">{{ item.unitPrice.toLocaleString() }}</span>
          <span class="Unit">円</span>
        </div>
      </div>
    </div>
    <div class="OrderNumInfo" v-if="readOnly">
      <span class="OrderNumLabel">数量：</span>
      <span>{{ item.orderNum }}</span>
    </div>
    <div class="InputWrapper" v-else>
      <Input
        type="number"
        showModifyButton
        :model-value="item.orderNum"
        :disabledNumAdd="isPurchaseNumEqualToStockNum"
        @numSubtract="handleSubtract"
        @numAdd="handleAdd"
      />
    </div>
  </div>

  <Modal :show="showDeleteConfirmModal" @close="showDeleteConfirmModal = false">
    <template v-slot:body>{{ cardDetailInfoString }}を削除します。</template>
    <template v-slot:footer>
      <Button
        text="キャンセル"
        mode="secondary"
        style="height: 42px; width: 100%"
        @click="showDeleteConfirmModal = false"
      />
      <Button
        text="削除"
        mode="distructive"
        style="height: 42px; width: 100%"
        @click="$emit('numSubtract')"
      />
    </template>
  </Modal>
</template>

<script lang="ts">
import { defineNuxtComponent } from "#app";
import { computed, ref } from "vue";
import {
  Input,
  ConditionLabel,
  Modal,
  Button,
  PMCardImage,
} from "@ka-nabellinc/ka-nabell-ui";
import type { PokemonCartItem } from "@/types/cart";

export default defineNuxtComponent({
  components: {
    Input,
    ConditionLabel,
    Modal,
    Button,
    PMCardImage,
  },
  props: {
    item: {
      type: Object as () => PokemonCartItem,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["numAdd", "numSubtract", "leaveCart"],
  setup(props, { emit }) {
    const showDeleteConfirmModal = ref(false);

    const cardInfoString = computed(() => {
      let infoString = "";

      if (props.item.product.pm_card.pack.expansion_mark) {
        infoString += `[${props.item.product.pm_card.pack.expansion_mark}]`;
      }

      infoString += `[${props.item.product.pm_card.rarity.rarity_abbreviation}]`;

      return infoString;
    });

    const cardDetailInfoString = computed(() => {
      let infoString = `${
        props.item.product.product_name
      }[状態${props.item.rank.toUpperCase()}]`;

      if (props.item.product.pm_card.pack.expansion_mark) {
        infoString += `[${props.item.product.pm_card.pack.expansion_mark}]`;
      }

      infoString += `[${props.item.product.pm_card.rarity.rarity_abbreviation}]`;

      return infoString;
    });

    const productPageLink = computed(
      () => `/pokemon/single/${props.item.product.inventory_product_id}`
    );

    const isPurchaseNumEqualToStockNum = computed(() => {
      const { product, orderNum, rank } = props.item;
      const targetInventory = product[`rank_${rank}_inventory`];
      if (!targetInventory) return false;
      const targetInventoryNum = targetInventory.inventory_num;
      return orderNum === targetInventoryNum;
    });

    const handleSubtract = () => {
      if (props.item.orderNum === 1) {
        showDeleteConfirmModal.value = true;
      } else {
        emit("numSubtract");
      }
    };

    const handleAdd = () => {
      // FIXME: 購入制限チェック
      if (isPurchaseNumEqualToStockNum.value) return;
      emit("numAdd");
    };

    return {
      showDeleteConfirmModal,
      cardInfoString,
      cardDetailInfoString,
      productPageLink,
      isPurchaseNumEqualToStockNum,
      handleSubtract,
      handleAdd,
    };
  },
});
</script>

<style scoped>
.Item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.Product {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0; /* NOTE: flexにおいては子要素の幅を必要なだけ確保しようとするため、この指定が必要 */
}

.ProductImageWrapper {
  width: 46px; /* NOTE: PMCardImageはwidth:100%を持つため、親要素のwidthを指定 */
}

.ItemInfo {
  flex: 1; /* NOTE: 真ん中の領域を可変にする */
  display: flex;
  flex-direction: column;
}

.InputWrapper {
  /* NOTE: デフォルトのwidth:100%が稀に優先されてInput要素が広がってしまう事象への対応として、Inputのwrapperを用意して固定幅を設定 */
  width: 88px;
}

.ProductName {
  font-size: 13px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: 0.13px;
  margin-bottom: 4px;

  overflow: hidden;
  display: -webkit-box;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.ProductInfo {
  font-size: 11px;
  line-height: 160%;
  letter-spacing: 0.11px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.UnitPrice {
  display: flex;
  gap: 4px;
  align-items: flex-end;
}

.Price {
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: 0.14px;
}

.Unit {
  font-size: 11px;
  line-height: 160%;
  letter-spacing: 0.11px;
}

.OrderNumInfo {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: 0.32px;
  white-space: nowrap;
}

.OrderNumLabel {
  font-size: 10px;
  font-weight: 400;
  line-height: 160%;
  letter-spacing: 0.1px;
}
</style>
